import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  HiCalendar,
  HiChartBar,
  HiArrowUp,
  HiArrowDown,
  HiX,
  HiFilter,
} from 'react-icons/hi';
import apiClient from '../../api/client';
import ChartPanel, { ChartErrorBoundary } from '../../components/ChartPanel';
import PeriodDateRangeModal, {
  applyComparePreset,
  computePreviousPeriod,
  defaultRange,
  formatDateRange,
} from '../../components/PeriodDateRangeModal';

const PAGE_SIZE = 500;

const metricOptions = [
  { key: 'hits_view', label: 'Просмотры', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'hits_view_search', label: 'Просмотры (поиск)', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'hits_view_pdp', label: 'Просмотры (карточка)', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'session_view', label: 'Сессии', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'hits_tocart', label: 'В корзину', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'orders_count', label: 'Заказы', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'returns', label: 'Возвраты', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'cancellations', label: 'Отмены', format: (v) => v?.toLocaleString('ru-RU') ?? '0' },
  { key: 'revenue', label: 'Выручка, ₽', format: (v) => (v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '0') },
  { key: 'ad_spend', label: 'Расход, ₽', format: (v) => (v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '0') },
  { key: 'profit', label: 'Прибыль, ₽', format: (v) => (v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '0') },
  { key: 'margin_pct', label: 'Маржа, %', format: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—') },
  { key: 'drr', label: 'ДРР, %', format: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—') },
  { key: 'position_category', label: 'Позиция в категории', format: (v) => (v != null ? Number(v).toFixed(1) : '—') },
  { key: 'open_to_cart_rate', label: 'Конв. просм. → корз., %', format: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '0%') },
  { key: 'cart_to_order_rate', label: 'Конв. корз. → заказ, %', format: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '0%') },
  { key: 'open_to_order_rate', label: 'Конв. просм. → заказ, %', format: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '0%') },
  { key: 'avg_check', label: 'Средний чек, ₽', format: (v) => (v != null ? Number(v).toFixed(2) : '—') },
];

const metricColors = {
  hits_view: '#4f46e5',
  hits_view_search: '#6366f1',
  hits_view_pdp: '#818cf8',
  session_view: '#a855f7',
  hits_tocart: '#ef4444',
  orders_count: '#10b981',
  returns: '#f59e0b',
  cancellations: '#dc2626',
  revenue: '#f97316',
  ad_spend: '#dc2626',
  profit: '#22c55e',
  margin_pct: '#84cc16',
  drr: '#0891b2',
  position_category: '#64748b',
  open_to_cart_rate: '#6366f1',
  cart_to_order_rate: '#0ea5e9',
  open_to_order_rate: '#ec4899',
  avg_check: '#8b5cf6',
};

const fmtInt = (v) => (v != null ? Number(v).toLocaleString('ru-RU') : '—');
const fmtMoney = (v) => (v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '—');
const fmtPct = (v) => (v != null && Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : '—');
const fmtAvgCheck = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');

const LOWER_IS_BETTER = new Set(['drr', 'returns', 'cancellations', 'position_category']);

function getDelta(current, compare) {
  if (compare === null || compare === undefined) return null;
  const cur = Number(current) || 0;
  const cmp = Number(compare);
  if (!Number.isFinite(cmp)) return null;
  const delta = cur - cmp;
  const percent = cmp !== 0 ? (delta / cmp) * 100 : (cur !== 0 ? 100 : 0);
  return { delta, percent };
}

function getCompareDeltaColorClass(metricKey, delta) {
  const isPositive = delta >= 0;
  if (LOWER_IS_BETTER.has(metricKey)) return isPositive ? 'text-red-600' : 'text-green-600';
  return isPositive ? 'text-green-600' : 'text-red-600';
}

function formatWithDelta(current, compare, formatter, metricKey) {
  const deltaData = getDelta(current, compare);
  if (deltaData === null) {
    return <div className="font-medium">{formatter(current)}</div>;
  }
  const { delta, percent } = deltaData;
  const percentText = `${delta >= 0 ? '+' : ''}${percent.toFixed(1)}%`;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="font-medium">{formatter(current)}</div>
      <div className={`text-xs font-medium ${getCompareDeltaColorClass(metricKey, delta)}`}>{percentText}</div>
      <div className="text-xs text-gray-500">{formatter(compare)}</div>
    </div>
  );
}

function ChartMetricToggle({ metric, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(metric); }}
      className={`p-1 rounded transition-colors ${
        selected ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
      }`}
      title={selected ? 'Убрать с графика' : 'Добавить на график'}
    >
      {selected ? <HiX className="w-4 h-4" /> : <HiChartBar className="w-4 h-4" />}
    </button>
  );
}

export default function OzonSalesFunnelStatistics({ clientId }) {
  const initialRange = defaultRange();
  const initialCompare = computePreviousPeriod(initialRange.from, initialRange.to);
  const [periodFrom, setPeriodFrom] = useState(initialRange.from);
  const [periodTo, setPeriodTo] = useState(initialRange.to);
  const [compareFrom, setCompareFrom] = useState(initialCompare.from);
  const [compareTo, setCompareTo] = useState(initialCompare.to);
  const [compareDirty, setCompareDirty] = useState(false);
  const [comparePreset, setComparePreset] = useState('previous');
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [groupBy, setGroupBy] = useState('day');
  const [selectedMetrics, setSelectedMetrics] = useState(['orders_count', 'revenue']);
  const [showCompareLines, setShowCompareLines] = useState(true);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState('');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortColumn, setSortColumn] = useState('revenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterText, setFilterText] = useState('');
  const [filterDebounced, setFilterDebounced] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterDebounceRef = useRef(null);

  useEffect(() => {
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setFilterDebounced(filterText);
      setPage(1);
    }, 400);
    return () => clearTimeout(filterDebounceRef.current);
  }, [filterText]);

  useEffect(() => {
    if (compareDirty || comparePreset === 'manual') return;
    if (!periodFrom || !periodTo) return;
    const range = applyComparePreset(comparePreset, periodFrom, periodTo);
    if (!range) return;
    setCompareFrom(range.from);
    setCompareTo(range.to);
  }, [periodFrom, periodTo, compareDirty, comparePreset]);

  const fetchSummary = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const params = {
        client_id: clientId,
        from: periodFrom,
        to: periodTo,
        group_by: groupBy,
      };
      if (compareFrom && compareTo) {
        params.compare_from = compareFrom;
        params.compare_to = compareTo;
      }
      const { data } = await apiClient.get('/ozon/funnel/stats', { params });
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось загрузить статистику');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [clientId, periodFrom, periodTo, compareFrom, compareTo, groupBy]);

  const fetchItems = useCallback(async () => {
    if (!clientId) return;
    setItemsLoading(true);
    setItemsError('');
    try {
      const params = {
        client_id: clientId,
        from: periodFrom,
        to: periodTo,
        page,
        page_size: PAGE_SIZE,
        sort_by: sortColumn,
        sort_dir: sortDirection,
      };
      if (compareFrom && compareTo) {
        params.compare_from = compareFrom;
        params.compare_to = compareTo;
      }
      if (filterDebounced.trim()) params.q = filterDebounced.trim();
      const { data } = await apiClient.get('/ozon/funnel/products', { params });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalItems(Number(data.total_items) || 0);
      setTotalPages(Number(data.total_pages) || 0);
    } catch (err) {
      setItems([]);
      setTotalItems(0);
      setTotalPages(0);
      setItemsError(err.response?.data?.message || 'Не удалось загрузить товары');
    } finally {
      setItemsLoading(false);
    }
  }, [clientId, periodFrom, periodTo, compareFrom, compareTo, page, sortColumn, sortDirection, filterDebounced]);

  useEffect(() => { if (clientId) fetchSummary(); }, [clientId, fetchSummary]);
  useEffect(() => { setPage(1); }, [periodFrom, periodTo, compareFrom, compareTo, clientId]);
  useEffect(() => { if (clientId && periodFrom && periodTo) fetchItems(); }, [clientId, periodFrom, periodTo, fetchItems]);

  const handleDateApply = ({
    periodFrom: nextFrom,
    periodTo: nextTo,
    compareFrom: nextCompareFrom,
    compareTo: nextCompareTo,
    compareDirty: nextCompareDirty,
    comparePreset: nextComparePreset,
  }) => {
    setPeriodFrom(nextFrom);
    setPeriodTo(nextTo);
    setCompareFrom(nextCompareFrom);
    setCompareTo(nextCompareTo);
    setCompareDirty(nextCompareDirty);
    setComparePreset(nextComparePreset);
  };

  const handleMetricToggle = (metric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric],
    );
  };

  const handleSort = (column) => {
    setPage(1);
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn('revenue');
      setSortDirection('desc');
    }
  };

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.period?.length && !stats?.timeSeries?.compare?.length) return [];
    const primary = stats.timeSeries.period.length > 0 ? stats.timeSeries.period : stats.timeSeries.compare ?? [];
    const secondary = stats.timeSeries.compare ?? [];
    return primary.map((row, index) => {
      const compareRow = secondary[index];
      const dataset = { label: row.label };
      selectedMetrics.forEach((metric) => {
        dataset[`period_${metric}`] = row[metric] ?? 0;
        dataset[`compare_${metric}`] = compareRow ? compareRow[metric] ?? null : null;
      });
      return dataset;
    });
  }, [stats, selectedMetrics]);

  const summaryCards = useMemo(() => {
    if (!stats?.period?.totals) return [];
    const t = stats.period.totals;
    const c = stats.compare?.totals;
    return [
      { key: 'hits_view', label: 'Просмотры', value: t.hits_view, compare: c?.hits_view, format: fmtInt },
      { key: 'hits_view_search', label: 'Просмотры (поиск)', value: t.hits_view_search, compare: c?.hits_view_search, format: fmtInt },
      { key: 'hits_tocart', label: 'В корзину', value: t.hits_tocart, compare: c?.hits_tocart, format: fmtInt },
      { key: 'orders_count', label: 'Заказы', value: t.orders_count, compare: c?.orders_count, format: fmtInt },
      { key: 'returns', label: 'Возвраты', value: t.returns, compare: c?.returns, format: fmtInt },
      { key: 'revenue', label: 'Выручка, ₽', value: t.revenue, compare: c?.revenue, format: fmtMoney },
      { key: 'ad_spend', label: 'Расход, ₽', value: t.ad_spend, compare: c?.ad_spend, format: fmtMoney },
      { key: 'profit', label: 'Прибыль, ₽', value: t.profit, compare: c?.profit, format: fmtMoney },
      { key: 'margin_pct', label: 'Маржа, %', value: t.margin_pct, compare: c?.margin_pct, format: fmtPct },
      { key: 'drr', label: 'ДРР, %', value: t.drr, compare: c?.drr, format: fmtPct },
      { key: 'position_category', label: 'Позиция', value: t.position_category, compare: c?.position_category, format: (v) => (v != null ? Number(v).toFixed(1) : '—') },
      { key: 'open_to_order_rate', label: 'Конв. → заказ', value: t.open_to_order_rate, compare: c?.open_to_order_rate, format: fmtPct },
      { key: 'avg_check', label: 'Средний чек', value: t.avg_check, compare: c?.avg_check, format: fmtAvgCheck },
    ];
  }, [stats]);

  const hasFilter = Boolean(filterDebounced.trim());

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-start">
        <button
          type="button"
          onClick={() => setDateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
        >
          <HiCalendar className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">{formatDateRange(periodFrom, periodTo)}</span>
        </button>
      </div>

      {loading && <div className="text-center text-gray-600">Загружается статистика…</div>}
      {error && <div className="text-center text-red-600">{error}</div>}

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {summaryCards.map((card) => {
              const deltaData = card.compare !== undefined ? getDelta(card.value, card.compare) : null;
              const deltaText = deltaData
                ? `${deltaData.percent >= 0 ? '+' : ''}${deltaData.percent.toFixed(1)}%`
                : null;
              return (
                <div key={card.key} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition-shadow">
                  <p className="text-xs font-medium text-gray-600 truncate" title={card.label}>{card.label}</p>
                  <p className="text-base font-bold text-gray-900 mb-0.5 tabular-nums">{card.format(card.value)}</p>
                  {deltaText && (
                    <span className={`text-xs font-semibold ${getCompareDeltaColorClass(card.key, deltaData.delta)}`}>
                      {deltaText}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-white/50 bg-white/85 backdrop-blur-sm shadow-sm p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Динамика</h3>
                <p className="text-sm text-gray-500">
                  {stats.period.from} → {stats.period.to}
                  {stats.compare && ` (сравнение: ${stats.compare.from} → ${stats.compare.to})`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCompareLines}
                    onChange={(e) => setShowCompareLines(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Показать период сравнения
                </label>
                <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100/80">
                  <span className="text-xs text-gray-500 px-2 py-1.5">Группировка:</span>
                  {[
                    { key: 'day', short: 'D', label: 'По дням' },
                    { key: 'week', short: 'W', label: 'По неделям' },
                    { key: 'month', short: 'M', label: 'По месяцам' },
                  ].map(({ key, short, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setGroupBy(key)}
                      title={label}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        groupBy === key ? 'bg-indigo-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800 hover:bg-white'
                      }`}
                    >
                      {short}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <ChartPanel height={320}>
              <ChartErrorBoundary label="Ozon">
                {chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-500">
                    Нет данных за выбранный период
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={320} debounce={50}>
                    <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                      <defs>
                        {selectedMetrics.map((metric) => {
                          const color = metricColors[metric] || '#6366f1';
                          return (
                            <linearGradient key={metric} id={`ozon-chart-fill-${metric}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                              <stop offset="70%" stopColor={color} stopOpacity={0.2} />
                              <stop offset="100%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(30, 41, 59, 0.95)',
                          border: 'none',
                          borderRadius: '10px',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                          padding: '12px 14px',
                        }}
                        labelStyle={{ color: '#94a3b8', fontSize: 12 }}
                        itemStyle={{ color: '#f1f5f9', fontSize: 13 }}
                        formatter={(value, name) => {
                          const metric = String(name).replace('period_', '').replace('compare_', '');
                          const fmt = metricOptions.find((m) => m.key === metric)?.format;
                          return [fmt ? fmt(value) : value, metricOptions.find((m) => m.key === metric)?.label || metric];
                        }}
                      />
                      {selectedMetrics.map((metric) => {
                        const color = metricColors[metric] || '#6366f1';
                        return (
                          <React.Fragment key={metric}>
                            <Area
                              type="monotone"
                              dataKey={`period_${metric}`}
                              name={`period_${metric}`}
                              fill={`url(#ozon-chart-fill-${metric})`}
                              stroke={color}
                              strokeWidth={2.5}
                              baseValue={0}
                              connectNulls
                              isAnimationActive={false}
                            />
                            {showCompareLines && stats.compare && (
                              <Line
                                type="monotone"
                                dataKey={`compare_${metric}`}
                                name={`compare_${metric}`}
                                stroke={color}
                                strokeOpacity={0.6}
                                dot={false}
                                strokeDasharray="5 5"
                                strokeWidth={1.5}
                                connectNulls
                                isAnimationActive={false}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartErrorBoundary>
            </ChartPanel>
          </div>
        </>
      )}

      <div className="bg-white rounded-xl shadow p-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 400px)' }}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-gray-700">Товары</h3>
          <span className="text-sm text-gray-500">
            {hasFilter ? `${totalItems} найдено` : `${totalItems.toLocaleString('ru-RU')} товаров`}
            {totalPages > 1 && ` · стр. ${page} из ${totalPages}`}
          </span>
        </div>

        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full text-left text-sm">
            <thead className="text-xs font-medium text-gray-600 border-b sticky top-0 z-10 bg-white">
              <tr>
                <th className="py-2 bg-white">
                  <div className="relative inline-flex items-center gap-2">
                    <span>Товар</span>
                    <button
                      type="button"
                      className={`p-1 rounded transition-colors ${hasFilter ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                      title="Фильтр по SKU или артикулу"
                      onClick={() => setFilterOpen((v) => !v)}
                    >
                      <HiFilter className="w-4 h-4" />
                    </button>
                    {filterOpen && (
                      <div className="absolute left-0 top-full mt-2 w-[320px] bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-20" onClick={(e) => e.stopPropagation()}>
                        <textarea
                          value={filterText}
                          onChange={(e) => setFilterText(e.target.value)}
                          rows={5}
                          className="w-full border border-gray-300 rounded-lg px-2 py-2 text-xs bg-white focus:ring-2 focus:ring-gray-300 resize-none"
                          placeholder={'SKU или артикул:\n700101\nART-OZ-1'}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button type="button" className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50" onClick={() => setFilterText('')}>Очистить</button>
                          <button type="button" className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50" onClick={() => setFilterOpen(false)}>Применить</button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                {[
                  { key: 'hits_view', label: 'Просмотры', metric: 'hits_view' },
                  { key: 'hits_view_search', label: 'Поиск', metric: 'hits_view_search' },
                  { key: 'hits_tocart', label: 'В корзину', metric: 'hits_tocart' },
                  { key: 'orders_count', label: 'Заказы', metric: 'orders_count' },
                  { key: 'returns', label: 'Возвраты', metric: 'returns' },
                  { key: 'revenue', label: 'Выручка, ₽', metric: 'revenue' },
                  { key: 'ad_spend', label: 'Расход, ₽', metric: 'ad_spend' },
                  { key: 'profit', label: 'Прибыль, ₽', metric: 'profit' },
                  { key: 'drr', label: 'ДРР, %', metric: 'drr' },
                  { key: 'open_to_cart_rate', label: 'Конв. → корз.', metric: 'open_to_cart_rate' },
                  { key: 'open_to_order_rate', label: 'Конв. → заказ', metric: 'open_to_order_rate' },
                  { key: 'avg_check', label: 'Средний чек', metric: 'avg_check' },
                ].map(({ key, label, metric }) => (
                  <th key={key} className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort(key)} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        {label}
                        {sortColumn === key && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric={metric} selected={selectedMetrics.includes(metric)} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {itemsLoading ? (
                <tr><td colSpan={13} className="py-8 text-center text-gray-500">Загрузка товаров…</td></tr>
              ) : itemsError ? (
                <tr><td colSpan={13} className="py-8 text-center text-red-600">{itemsError}</td></tr>
              ) : totalItems === 0 ? (
                <tr><td colSpan={13} className="py-8 text-center text-gray-500">Нет товаров за выбранный период</td></tr>
              ) : (
                items.map((row) => {
                  const cmp = row.compareMetrics;
                  return (
                    <tr key={row.sku} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="py-2 max-w-[280px]">
                        <div className="font-medium text-gray-900 truncate" title={row.title}>{row.title || `SKU ${row.sku}`}</div>
                        <div className="text-xs text-gray-500">{row.offer_id ? `${row.offer_id} · ` : ''}SKU {row.sku}</div>
                      </td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.hits_view, cmp?.hits_view, fmtInt, 'hits_view')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.hits_view_search, cmp?.hits_view_search, fmtInt, 'hits_view_search')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.hits_tocart, cmp?.hits_tocart, fmtInt, 'hits_tocart')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.orders_count, cmp?.orders_count, fmtInt, 'orders_count')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.returns, cmp?.returns, fmtInt, 'returns')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.revenue, cmp?.revenue, fmtMoney, 'revenue')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.ad_spend, cmp?.ad_spend, fmtMoney, 'ad_spend')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.profit, cmp?.profit, fmtMoney, 'profit')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.drr, cmp?.drr, fmtPct, 'drr')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.funnel?.open_to_cart_rate, cmp?.funnel?.open_to_cart_rate, fmtPct, 'open_to_cart_rate')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.funnel?.open_to_order_rate, cmp?.funnel?.open_to_order_rate, fmtPct, 'open_to_order_rate')}</td>
                      <td className="py-2 tabular-nums">{formatWithDelta(row.funnel?.avg_check, cmp?.funnel?.avg_check, fmtAvgCheck, 'avg_check')}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              Показано {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalItems)} из {totalItems.toLocaleString('ru-RU')}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || itemsLoading} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Назад</button>
              <span className="text-sm text-gray-600">{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages || itemsLoading} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Вперёд</button>
            </div>
          </div>
        )}
      </div>

      <PeriodDateRangeModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        periodFrom={periodFrom}
        periodTo={periodTo}
        compareFrom={compareFrom}
        compareTo={compareTo}
        compareDirty={compareDirty}
        comparePreset={comparePreset}
        onApply={handleDateApply}
      />
    </div>
  );
}
